import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    useTheme,
    IconButton,
} from '@mui/material';
import LineChart from '../Common/Charts/LineChart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationAddIcon from '@mui/icons-material/NotificationAdd';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import DeleteIcon from '@mui/icons-material/Delete';
import ErrorIcon from '@mui/icons-material/Error';
import LicensePlate from '../LicensePlate';
import { useTranslation } from 'react-i18next';
import useToast, { ToastPosition, ToastType } from '../../hooks/useToast';
import ActionButtons from './ActionButtons';
import MarkIrrelevantSuccess from './MarkIrrelevantSuccess';
import MarkIrrelevantDialog from './MarkIrrelevantDialog';
import AlertDialog from '../AlertDialog/AlertDialog';
import useEntriesService from '../../services/useEntriesService';
import LoadingPage from '../../pages/loading/LoadingPage';
import { GetLicensePlateEntriesResponse } from '../../types/Entries';
import { eventBus } from '../../common/eventBus';
import { ClientAlertsProvider } from '../../context/administration/ClientAlertsContext';
import useCanAccess from '../../hooks/useCanAccess';

interface ViewNumberPlateDetailsDialogProps {
    open: boolean;
    onClose: () => void;
    licensePlate: string;
}

function ViewNumberPlateDetailsDialog({ open, onClose, licensePlate }: ViewNumberPlateDetailsDialogProps) {
    const [loading, setLoading] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState<null | HTMLElement>(null);
    const [createAlertDialogOpen, setCreateAlertDialogOpen] = useState(false);
    const [entriesData, setEntriesData] = useState<GetLicensePlateEntriesResponse>();
    const [markIrrelevantDialogInfo, setMarkIrrelevantDialogInfo] = useState<{ open: boolean }>({ open: false });
    const lineChartParentRef = useRef<HTMLDivElement>(null);

    const { getLicensePlateEntries, markCarAsIrrelevant, deleteAllEntriesForLicensePlate } = useEntriesService();
    const theme = useTheme();
    const { t } = useTranslation("common");
    const { showToast } = useToast();
    const { canAccessAction } = useCanAccess();

    const entriesPerMonth = entriesData?.entriesPerMonth;
    const entries = entriesData?.entries.entries;
    const isIrrelevant = entries?.[0]?.isirrelevant;

    const handleMarkIrrelevant = useCallback((licensePlate: string) => {
        // Only show dialog when marking as irrelevant (not when unmarking)
        if (!isIrrelevant) {
            setMarkIrrelevantDialogInfo({ open: true });
        } else {
            // When unmarking as irrelevant, don't need reason
            showToast({
                message: () => <MarkIrrelevantSuccess licensePlate={licensePlate} markedAsIrrelevant={false} />,
                type: ToastType.PROMISE,
                promise: markCarAsIrrelevant(licensePlate, false),
                duration: 10000,
            });
        }
    }, [isIrrelevant, markCarAsIrrelevant, showToast]);

    const handleMarkIrrelevantConfirm = useCallback(async (reason: string) => {
        showToast({
            message: () => <MarkIrrelevantSuccess licensePlate={licensePlate} markedAsIrrelevant={false} />,
            type: ToastType.PROMISE,
            promise: markCarAsIrrelevant(licensePlate, true, reason),
            duration: 10000,
        });
        
        setMarkIrrelevantDialogInfo({ open: false });
    }, [licensePlate, markCarAsIrrelevant, showToast]);

    const handleDeleteAllEntries = useCallback(async (plate: string) => {
        showToast({
            message: "",
            confirmMessage: t("search.licensePlate.confirmDeleteAllEntries", { licensePlate: plate }),
            type: ToastType.CONFIRM,
            confirmCallback: async () => {
                try {
                    await deleteAllEntriesForLicensePlate(plate);
                    onClose();
                    showToast({
                        message: t("search.licensePlate.deleteAllEntriesSuccess", { licensePlate: plate }),
                        type: ToastType.SUCCESS,
                    });
                } catch {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                        position: ToastPosition.TOP_CENTER,
                    });
                }
            },
        });
    }, [deleteAllEntriesForLicensePlate, onClose, showToast, t]);

    useEffect(() => {
        if (open && licensePlate) {
            setLoading(true);
            getLicensePlateEntries(licensePlate)
                .then((data) => {
                    setEntriesData(data);
                })
                .catch(() => {
                    showToast({
                        message: t("common.unknownError"),
                        type: ToastType.ERROR,
                        position: ToastPosition.TOP_CENTER,
                    });
                })
                .finally(() => {
                    setLoading(false);
                });

            // Listen for external updates to the irrelevant status, so action buttons can be updated
            const handler = () => {
                getLicensePlateEntries(licensePlate)
                    .then((data) => {
                        setEntriesData(data);
                    })
            };

            eventBus.on('carMarkedAsIrrelevant', handler);

            return () => {
                eventBus.off('carMarkedAsIrrelevant', handler);
            }
        }
    }, [open, licensePlate, getLicensePlateEntries, showToast, t]);

    const actions = useMemo(() => {
        const buttons = [];
        if (canAccessAction('markIrrelevant')) {
            buttons.push({
                key: 'mark-irrelevant',
                icon: isIrrelevant ? <NotificationImportantIcon fontSize='small' /> : <ErrorIcon fontSize='small' />,
                label: isIrrelevant ? t("search.markAsRelevant") : t("search.markAsIrrelevant"),
                onClick: () => handleMarkIrrelevant(licensePlate),
            });
        }
        if (canAccessAction('createAlert')) {
            buttons.push(
                {
                    key: 'create-alert',
                    icon: <NotificationAddIcon fontSize='small' />,
                    label: t("search.createAlert"),
                    onClick: () => setCreateAlertDialogOpen(true),
                });
        }
        if (canAccessAction('deleteAllEntriesForLicensePlate')) {
            buttons.push({
                key: 'delete',
                icon: <DeleteIcon fontSize='small' />,
                label: t("search.delete"),
                onClick: () => handleDeleteAllEntries(licensePlate),
            });
        }
        return buttons;
    }, [canAccessAction, isIrrelevant, licensePlate, t, handleDeleteAllEntries, handleMarkIrrelevant]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            {loading ? (
                <LoadingPage />
            ) : (
                <>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                                <Box alignItems="center" display="flex" gap={1}>
                                    <LicensePlate licenseNumber={licensePlate} />
                                    {t("viewNumberPlateDetails.totalEntries")}: {entriesData?.entries.totalCount}
                                </Box>
                                {
                                    isIrrelevant &&
                                    (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            {/* TODO use color from theme */}
                                            <ErrorIcon fontSize="medium" sx={{ color: 'red' }} />
                                            {t("viewNumberPlateDetails.clientIsMarkedAsIrrelevant")}
                                        </Box>
                                    )
                                }
                            </Box>

                            <Box>
                                <IconButton onClick={(event) => setPopoverOpen(event.currentTarget)}>
                                    <MoreVertIcon />
                                </IconButton>
                                <ActionButtons
                                    isPopoverOpen={!!popoverOpen}
                                    anchorEl={popoverOpen}
                                    onPopoverClose={() => setPopoverOpen(null)}
                                    buttons={actions}
                                />
                            </Box>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box
                            sx={{
                                width: '100%',
                                height: { xs: 300, sm: 400 },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                mt: 5,
                            }}
                            ref={lineChartParentRef}
                        >

                            <LineChart
                                parentRef={lineChartParentRef}
                                colorPalette={[theme.palette.primary.main]}
                                data={{
                                    xAxis: {
                                        data: entriesPerMonth?.months ?? [],
                                    },
                                    series: [
                                        {
                                            id: 'today',
                                            label: t("viewNumberPlateDetails.entriesPerMonth"),
                                            data: entriesPerMonth?.countEntries ?? [],
                                            area: true,
                                        },
                                    ],
                                }}
                            />
                        </Box>
                    </DialogContent>
                </>
            )}
            <DialogActions>
                <Button onClick={onClose}>
                    {t("common.close")}
                </Button>
            </DialogActions>
            <AlertDialog
                open={createAlertDialogOpen}
                onClose={() => {
                    setCreateAlertDialogOpen(false);
                }}
                initialValues={{ numberPlate: licensePlate }}
            />
            <MarkIrrelevantDialog
                open={markIrrelevantDialogInfo.open}
                licensePlate={licensePlate}
                onConfirm={handleMarkIrrelevantConfirm}
                onCancel={() => {
                    setMarkIrrelevantDialogInfo({ open: false });
                }}
            />
        </Dialog>
    );
}

const AlertDialogWithAlertsContext = (props: ViewNumberPlateDetailsDialogProps) => (
    <ClientAlertsProvider>
        <ViewNumberPlateDetailsDialog {...props} />
    </ClientAlertsProvider>
)

export default AlertDialogWithAlertsContext;

